import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Coffee, RefreshCw } from 'lucide-react'
import { apiFetch } from '../../config/api'
import { type PageStat, StatTable } from './statistikShared'
import PageHeader from '../../components/Dashboard/PageHeader'

export default function DashboardStatistikBrunch() {
  const [pages, setPages] = useState<PageStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getAuthToken = () => localStorage.getItem('dashboard_token') || ''

  const fetchStats = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiFetch('/analytics/pages', {
        headers: { Authorization: getAuthToken() },
      })
      if (!res.ok) {
        if (res.status === 401) return
        throw new Error('Statistiken konnten nicht geladen werden.')
      }
      const data = await res.json()
      if (data.success && Array.isArray(data.pages)) {
        const all = data.pages.map((p: { path: string; today: string | number; last7d: string | number }) => ({
          path: p.path || '/',
          today: String(p.today ?? 0),
          last7d: String(p.last7d ?? 0),
        }))
        setPages(all.filter((p: PageStat) => (p.path || '').startsWith('/brunch')))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const brunchToday = pages.reduce((s, p) => s + (parseInt(p.today, 10) || 0), 0)
  const brunch7d = pages.reduce((s, p) => s + (parseInt(p.last7d, 10) || 0), 0)
  const max7dBrunch = Math.max(1, ...pages.map((p) => parseInt(p.last7d, 10) || 0))

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        title="Brunch-Statistiken"
        description="Aufrufe der Brunch-Seiten."
        actions={
          <button
            onClick={fetchStats}
            disabled={loading}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-800 bg-gray-950 text-sm text-zinc-300 hover:bg-gray-900 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        }
      />

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400"
        >
          {error}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-xl sm:rounded-2xl bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl border border-emerald-500/20 overflow-hidden min-w-0"
      >
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-emerald-500/20 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4 bg-emerald-500/5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/20">
              <Coffee className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-white">Brunch-Seiten</h2>
              <p className="text-gray-400 text-xs sm:text-sm">Aufrufe pro Seite (Übersicht und Brunch-Details)</p>
            </div>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-right">
              <p className="text-xl sm:text-2xl font-bold text-white tabular-nums">{loading ? '—' : brunchToday}</p>
              <p className="text-xs text-gray-400">Heute</p>
            </div>
            <div className="w-px h-8 sm:h-10 bg-white/10" />
            <div className="text-right">
              <p className="text-xl sm:text-2xl font-bold text-emerald-400 tabular-nums">{loading ? '—' : brunch7d}</p>
              <p className="text-xs text-gray-400">7 Tage</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 sm:p-12 text-center text-gray-400">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-70" />
            <p>Statistiken werden geladen …</p>
          </div>
        ) : pages.length === 0 ? (
          <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500 text-sm">
            Noch keine Aufrufe von Brunch-Seiten.
          </div>
        ) : (
          <div className="overflow-x-auto min-w-0 w-full">
            <StatTable rows={pages} maxBar={max7dBrunch} startIndex={0} />
          </div>
        )}
      </motion.div>
    </div>
  )
}
